import { ConjunctionMode } from "@redotech/redo-customer-segments-common/segments/segment-types";
import { useTranslation } from "@redotech/redo-i18n";
import PlusIcon from "@redotech/redo-icons/plus.svg";
import TrashIcon from "@redotech/redo-icons/trash.svg";
import { blockBorderProps } from "@redotech/redo-web-marketing/marketing/segments/segment-builder-editor";
import { SegmentConjunctionSelect } from "@redotech/redo-web-marketing/marketing/segments/segment-conjunction-select";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, ReactNode, useState } from "react";
import { ConditionSummarySegment } from "../../nodes/condition-summary";
import { ConditionSummaryText } from "../../nodes/condition-summary-text";

export const TriggerConditionsSection = memo(function TriggerConditionsSection({
  title,
  addButtonText,
  addCondition,
  conditionCount,
  conjunctionMode,
  setConjunctionMode,
  conjunctionText,
  singleConditionText,
  children,
}: {
  title: string;
  addButtonText: string;
  addCondition: () => void;
  conditionCount: number;
  conjunctionMode: ConjunctionMode;
  setConjunctionMode: (conjunctionMode: ConjunctionMode) => void;
  /** Follows "All"/"Any" in the select, so it reads as one sentence. */
  conjunctionText: string;
  /** Replaces the select when there is nothing to combine. */
  singleConditionText: string;
  children: ReactNode;
}) {
  return (
    <Flex dir="column" gap="xl">
      <Flex dir="column" gap="md">
        <Flex align="center" justify="space-between">
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            {title}
          </Text>
          <RedoButton
            hierarchy="secondary"
            IconLeading={PlusIcon}
            onClick={addCondition}
            size="xs"
            text={addButtonText}
          />
        </Flex>
        {conditionCount > 1 ? (
          <SegmentConjunctionSelect
            mode={conjunctionMode}
            setMode={setConjunctionMode}
            textAfterConjunction={conjunctionText}
          />
        ) : (
          <Text fontSize="xxs" textColor="secondary">
            {singleConditionText}
          </Text>
        )}
      </Flex>
      {children}
    </Flex>
  );
});

export const TriggerConditionCard = memo(function TriggerConditionCard({
  label,
  summary,
  isValid,
  removeCondition,
  children,
}: {
  label: string;
  summary: ConditionSummarySegment[];
  isValid: boolean;
  removeCondition: () => void;
  /** The editor, shown while expanded. */
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(isValid);

  return (
    <Flex
      {...blockBorderProps}
      // Shouldn't be a possible state in the current implementation, but just in case
      {...(!isValid && collapsed ? { borderColor: "error" } : {})}
      dir="column"
      gap="xl"
      p="xl"
    >
      {collapsed ? (
        <Flex dir="column" gap="sm">
          <Text fontSize="xxs" textColor="secondary">
            {label}
          </Text>
          <ConditionSummaryText segments={summary} />
        </Flex>
      ) : (
        children
      )}
      <Flex align="center">
        <RedoButton
          disabled={!collapsed && !isValid}
          hierarchy="secondary"
          onClick={() => setCollapsed((c) => !c)}
          size="xs"
          text={collapsed ? t("Edit") : t("Save")}
        />
        <RedoButton
          aria-label={t("Remove condition")}
          hierarchy="tertiary"
          IconLeading={TrashIcon}
          onClick={removeCondition}
          size="xs"
        />
      </Flex>
    </Flex>
  );
});
